import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Video } from "lucide-react";
import { scrollTo } from "@/components/Navbar";

const LINES = ["Dubai's skyline,", "curated for", "your next chapter."];

const TICKER = [
  "DLD 2025: AED 761B in transactions — a record year",
  "Off-plan now ≈ 60% of all Dubai sales",
  "0% income tax · 0% capital gains tax",
  "Golden Visa from AED 2M property investment",
  "Average rental yields 6–9% across prime communities",
  "DLD First-Time Home Buyer Program now live",
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0.2]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex flex-col overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src="https://images.unsplash.com/flagged/photo-1559717201-fbb671ff56b7?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Dubai skyline illuminated at dusk with Burj Khalifa"
          className="w-full h-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 hero-vignette" />

      <motion.div style={{ opacity: fade }} className="relative z-10 flex-1 flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-28 pb-16">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="text-xs uppercase tracking-[0.35em] text-gold-light/90 font-medium mb-8"
            data-testid="hero-eyebrow"
          >
            Haven &amp; Hill Properties · Dubai
          </motion.p>

          <h1 className="font-serif text-4xl sm:text-5xl lg:text-7xl tracking-tight leading-[1.08] text-[#F8F6F0] max-w-4xl">
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 1 ? "italic gold-text" : ""}`}
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.35 + i * 0.16, duration: 1, ease: [0.22, 1, 0.36, 1] }}
                  data-testid={`hero-headline-line-${i + 1}`}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.9 }}
            className="mt-8 max-w-xl text-sm sm:text-base text-zinc-300 leading-relaxed"
            data-testid="hero-subtext"
          >
            Twenty years of quiet leadership in Dubai real estate — guiding first-time buyers and
            seasoned investors through every RERA-regulated step, from shortlist to title deed.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.25, duration: 0.9 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-explore-guide-btn"
              onClick={() => scrollTo("guide")}
              className="inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-semibold text-ink hover:bg-gold-hover transition-colors"
            >
              Explore the Buyer's Guide
              <ArrowDown size={16} />
            </button>
            <button
              data-testid="hero-book-call-btn"
              onClick={() => scrollTo("booking")}
              className="inline-flex items-center gap-2 rounded-full border border-gold/40 px-7 py-3.5 text-sm font-medium text-gold-pale hover:bg-gold/10 transition-colors"
            >
              <Video size={16} />
              Book a Zoom or Phone Call
            </button>
          </motion.div>
        </div>
      </motion.div>

      <div className="relative z-10 border-t border-white/10 glass" data-testid="market-ticker">
        <div className="overflow-hidden ticker-mask py-3.5">
          <div className="flex whitespace-nowrap animate-marquee w-max">
            {[...TICKER, ...TICKER].map((t, i) => (
              <span key={i} className="mx-8 text-xs tracking-[0.18em] uppercase text-zinc-400 flex items-center gap-8">
                {t}
                <span className="inline-block w-1.5 h-1.5 rotate-45 bg-gold/70" />
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
